class ProductGalleryLightbox {
  constructor() {
    this._el = null;
    this._slider = null;
    this._flickity = null;
    this._images = [];
    this._keyHandler = null;
    this._isOpen = false;
    this._sourceGallery = null;
    this._thumbnails = null;
    this._thumbnailScroll = null;
    this._thumbnailHandler = null;
    this._thumbnailScrollHandler = null;
    this._activeThumbnailIndicator = null;
    this._featuredToggle = null;
    this._captionPill = null;
    this._captionFallback = 'Product image';
    this._activeIndex = 0;
    this._selectHandler = null;
    this._openToken = 0;
    this._resizeHandler = null;
    this._resizeTimeout = null;
    this._overlay = null;
    this._previousButton = null;
    this._nextButton = null;
  }

  _getFlickity() {
    if (this._flickity) return this._flickity;
    if (!this._slider || !window.Flickity) return null;
    return Flickity.data(this._slider) || null;
  }

  _navigate(direction) {
    const flkty = this._getFlickity();
    if (!flkty) return;
    if (direction > 0) {
      flkty.next();
    } else {
      flkty.previous();
    }
  }

  _destroyFlickity() {
    if (this._flickity) {
      if (this._selectHandler) {
        this._flickity.off('select', this._selectHandler);
        this._selectHandler = null;
      }
      this._flickity.destroy();
      this._flickity = null;
    }
    if (this._slider) {
      this._slider.classList.remove('flickity-enabled');
    }
  }

  _getCaption(index) {
    const image = this._images[index];
    return image?.caption?.trim() || image?.alt?.trim() || this._captionFallback;
  }

  _clearThumbnails() {
    if (this._thumbnailScroll) {
      this._thumbnailScroll.innerHTML = '';
    }
    if (this._thumbnails) {
      this._activeThumbnailIndicator = null;
      this._thumbnails.classList.remove(
        'pgl__thumbnails--has-left-overflow',
        'pgl__thumbnails--has-right-overflow',
        'pgl__thumbnails--has-top-overflow',
        'pgl__thumbnails--has-bottom-overflow',
        'pgl__thumbnails--animated-active'
      );
    }
  }

  _buildThumbnails() {
    if (!this._thumbnailScroll) return;

    this._clearThumbnails();

    this._images.forEach((image, index) => {
      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'pgl__thumbnail';
      thumbnail.dataset.index = String(index);
      thumbnail.setAttribute(
        'aria-label',
        `${this._el.dataset.galleryImageLabel || 'Gallery image'} ${index + 1}: ${this._getCaption(index)}`
      );
      thumbnail.setAttribute('aria-current', index === this._activeIndex ? 'true' : 'false');

      const thumbnailImage = document.createElement('img');
      thumbnailImage.src = image.src;
      thumbnailImage.alt = '';
      thumbnailImage.loading = 'lazy';
      thumbnailImage.decoding = 'async';
      thumbnail.appendChild(thumbnailImage);
      this._thumbnailScroll.appendChild(thumbnail);
    });

    this._activeThumbnailIndicator = document.createElement('span');
    this._activeThumbnailIndicator.className = 'pgl__thumbnail-indicator';
    this._activeThumbnailIndicator.setAttribute('aria-hidden', 'true');
    this._thumbnailScroll.appendChild(this._activeThumbnailIndicator);
  }

  _updateThumbnails() {
    if (!this._thumbnailScroll) return;

    const flkty = this._getFlickity();
    const index = flkty ? flkty.selectedIndex : this._activeIndex;
    this._activeIndex = index;

    this._thumbnailScroll.querySelectorAll('.pgl__thumbnail').forEach((thumbnail, thumbnailIndex) => {
      const isCurrent = thumbnailIndex === index;
      thumbnail.setAttribute('aria-current', isCurrent ? 'true' : 'false');
      if (isCurrent) {
        thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    });
    this._updateThumbnailEdgeFades();
    this._updateActiveThumbnailIndicator();
  }

  _updateThumbnailEdgeFades() {
    if (!this._thumbnails || !this._thumbnailScroll) return;

    const maxScrollLeft = Math.max(
      0,
      this._thumbnailScroll.scrollWidth - this._thumbnailScroll.clientWidth
    );
    const maxScrollTop = Math.max(
      0,
      this._thumbnailScroll.scrollHeight - this._thumbnailScroll.clientHeight
    );
    const tolerance = 1;
    const hasLeftOverflow = this._thumbnailScroll.scrollLeft > tolerance;
    const hasRightOverflow = this._thumbnailScroll.scrollLeft < maxScrollLeft - tolerance;
    const hasTopOverflow = this._thumbnailScroll.scrollTop > tolerance;
    const hasBottomOverflow = this._thumbnailScroll.scrollTop < maxScrollTop - tolerance;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const maxScroll = isMobile ? maxScrollLeft : maxScrollTop;
    const scrollPosition = isMobile
      ? this._thumbnailScroll.scrollLeft
      : this._thumbnailScroll.scrollTop;
    const hasOverflow = maxScroll > tolerance;
    const scrollProgress = hasOverflow
      ? Math.min(1, Math.max(0, scrollPosition / maxScroll))
      : 0;
    const fadeSize = hasOverflow ? 10 : 0;

    this._thumbnailScroll.style.setProperty(
      '--pgl-mask-start',
      `${scrollProgress * fadeSize}%`
    );
    this._thumbnailScroll.style.setProperty(
      '--pgl-mask-end',
      `${100 - (1 - scrollProgress) * fadeSize}%`
    );

    this._thumbnails.classList.toggle(
      'pgl__thumbnails--has-left-overflow',
      hasLeftOverflow
    );
    this._thumbnails.classList.toggle(
      'pgl__thumbnails--has-right-overflow',
      hasRightOverflow
    );
    this._thumbnails.classList.toggle(
      'pgl__thumbnails--has-top-overflow',
      hasTopOverflow
    );
    this._thumbnails.classList.toggle(
      'pgl__thumbnails--has-bottom-overflow',
      hasBottomOverflow
    );
  }

  _updateActiveThumbnailIndicator(animate = true) {
    if (!this._thumbnails || !this._activeThumbnailIndicator) return;

    const activeThumbnail = this._thumbnailScroll.querySelector(
      `.pgl__thumbnail[data-index="${this._activeIndex}"]`
    );
    if (!activeThumbnail) return;

    this._activeThumbnailIndicator.classList.toggle(
      'pgl__thumbnail-indicator--instant',
      !animate
    );
    this._activeThumbnailIndicator.style.width = `${activeThumbnail.offsetWidth}px`;
    this._activeThumbnailIndicator.style.height = `${activeThumbnail.offsetHeight}px`;
    this._activeThumbnailIndicator.style.transform = `translate(${
      activeThumbnail.offsetLeft
    }px, ${activeThumbnail.offsetTop}px)`;
    this._thumbnails.classList.add('pgl__thumbnails--animated-active');

    if (!animate) {
      requestAnimationFrame(() => {
        this._activeThumbnailIndicator?.classList.remove('pgl__thumbnail-indicator--instant');
      });
    }
  }

  _getFeaturedProductsButton(index = this._activeIndex) {
    if (!this._sourceGallery) return null;
    const sourceIndex = this._images[index]?.sourceIndex ?? index;

    const selectors = [
      `.flickity-slider-slide[data-index="${sourceIndex}"]`,
      `.product-main-slide[data-index="${sourceIndex}"]`,
    ];

    for (const selector of selectors) {
      const slide = this._sourceGallery.querySelector(selector);
      const button = slide?.querySelector('.nw-media-related-product-button');
      if (button) return button;
    }

    return null;
  }

  _updateFeaturedProducts(index = this._activeIndex) {
    if (!this._featuredToggle) return;

    const hasFeaturedProducts = Boolean(this._getFeaturedProductsButton(index));
    const hasCaption = Boolean(this._images[index]?.caption?.trim());
    this._featuredToggle.hidden = !hasFeaturedProducts;
    this._featuredToggle.setAttribute('aria-hidden', hasFeaturedProducts ? 'false' : 'true');

    if (this._captionPill) {
      this._captionPill.hidden = !hasCaption;
      this._captionPill.setAttribute('aria-hidden', hasCaption ? 'false' : 'true');
    }
  }

  _openFeaturedProducts() {
    const sourceButton = this._getFeaturedProductsButton();
    if (!sourceButton) return;

    sourceButton.click();
  }

  _closeFeaturedProducts() {
    const popup = document.getElementById('nw-media-popup-box-container');
    if (
      !popup ||
      (!popup.classList.contains('active') &&
        !popup.classList.contains('pgl-featured-products-drawer--closing'))
    ) {
      return false;
    }

    popup.querySelector('.popup-bg.close-popup')?.click();
    return true;
  }

  _initFlickity(startIndex, openToken) {
    if (!this._isOpen || openToken !== this._openToken) return;
    if (!window.Flickity || !this._slider) return;

    this._flickity = new Flickity(this._slider, {
      cellSelector: '.pgl__slide',
      cellAlign: 'left',
      contain: true,
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      draggable: true,
      percentPosition: false,
      setGallerySize: false,
      initialIndex: startIndex,
    });

    this._selectHandler = () => {
      this._activeIndex = this._getFlickity()?.selectedIndex ?? this._activeIndex;
      this._moveOverlayToSlide(this._activeIndex);
      this._updateCaption();
      this._updateThumbnails();
      this._updateFeaturedProducts();
      this._notifyChange();
    };
    this._flickity.on('select', this._selectHandler);
    this._moveOverlayToSlide(startIndex);
    this._updateCaption();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._isOpen || openToken !== this._openToken) return;
        this._flickity?.resize();
      });
    });
  }

  _buildEl() {
    if (this._el) return;

    this._el = document.getElementById('product-gallery-lightbox');
    if (!this._el) return;

    this._slider = this._el.querySelector('.pgl__slider');
    this._overlay = this._el.querySelector('.pgl__overlay');
    this._thumbnails = this._el.querySelector('.pgl__thumbnails');
    this._thumbnailScroll = this._el.querySelector('.pgl__thumbnails-scroll');
    this._featuredToggle = this._el.querySelector('.pgl__pill--featured');
    this._captionPill = this._el.querySelector('.pgl__pill--caption');
    this._captionFallback = this._el.dataset.captionFallback || 'Product image';

    this._el.querySelector('.pgl__backdrop').addEventListener('click', () => this.close());
    this._el.querySelector('.pgl__close').addEventListener('click', () => this.close());

    this._previousButton = this._el.querySelector('.pgl__nav--prev');
    this._nextButton = this._el.querySelector('.pgl__nav--next');
    this._previousButton?.addEventListener('click', () => this._navigate(-1));
    this._nextButton?.addEventListener('click', () => this._navigate(1));

    this._thumbnailHandler = (event) => {
      const thumbnail = event.target.closest('.pgl__thumbnail');
      if (!thumbnail) return;
      const index = Number(thumbnail.dataset.index);
      if (Number.isNaN(index)) return;
      this._getFlickity()?.select(index);
    };
    this._thumbnailScroll?.addEventListener('click', this._thumbnailHandler);
    this._thumbnailScrollHandler = () => {
      this._updateThumbnailEdgeFades();
      this._updateActiveThumbnailIndicator(false);
    };
    this._thumbnailScroll?.addEventListener('scroll', this._thumbnailScrollHandler, {
      passive: true,
    });
    this._featuredToggle?.addEventListener('click', () => this._openFeaturedProducts());
  }

  _waitForActiveImage(image, openToken) {
    if (!image) return;

    const showControls = () => {
      if (!this._isOpen || openToken !== this._openToken) return;

      const activeImage =
        this._flickity?.selectedElement?.querySelector('.pgl__media > img') ||
        this._slider.children[this._activeIndex]?.querySelector('.pgl__media > img');
      if (activeImage !== image) return;

      this._flickity?.resize();
      this._el.classList.remove('is-loading');
    };

    const showControlsWhenReady = () => {
      if (!this._isOpen || openToken !== this._openToken) return;

      const activeImage =
        this._flickity?.selectedElement?.querySelector('.pgl__media > img') ||
        this._slider.children[this._activeIndex]?.querySelector('.pgl__media > img');
      if (activeImage !== image) return;

      showControls();
    };

    const decodeImage = () => {
      if (!image.complete || !image.naturalWidth) return;
      if (typeof image.decode !== 'function') return showControlsWhenReady();

      image.decode().then(showControlsWhenReady).catch(() => {
        if (image.naturalWidth) showControlsWhenReady();
      });
    };

    image.addEventListener('load', decodeImage, { once: true });

    if (image.complete && image.naturalWidth) decodeImage();
  }

  open(images, startIndex, sourceGallery) {
    this._buildEl();
    if (!this._el) return;

    this._openToken += 1;
    const openToken = this._openToken;

    this._images = images;
    this._sourceGallery = sourceGallery || null;
    const sourceImageIndex = images.findIndex((image) => image.sourceIndex === startIndex);
    this._activeIndex = images.length
      ? sourceImageIndex >= 0
        ? sourceImageIndex
        : Math.max(0, Math.min(startIndex, images.length - 1))
      : 0;
    this._el.classList.add('is-loading');
    this._updateFeaturedProducts();

    this._destroyFlickity();
    this._slider.innerHTML = '';
    this._buildThumbnails();

    images.forEach((img, i) => {
      const slide = document.createElement('div');
      slide.className = 'pgl__slide';
      const media = document.createElement('div');
      media.className = 'pgl__media';
      const imgEl = document.createElement('img');
      imgEl.src = img.src;
      imgEl.alt = img.alt || this._captionFallback;
      imgEl.loading = i === this._activeIndex ? 'eager' : 'lazy';
      imgEl.decoding = 'async';
      media.appendChild(imgEl);
      slide.appendChild(media);
      this._slider.appendChild(slide);
    });

    this._moveOverlayToSlide(this._activeIndex);

    this._el.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('pgl-lightbox-open');
    this._isOpen = true;
    this._waitForActiveImage(
      this._slider.children[this._activeIndex]?.querySelector('.pgl__media > img'),
      openToken
    );
    this._updateThumbnailEdgeFades();
    this._updateActiveThumbnailIndicator(false);

    this._bindResizeHandler();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this._isOpen || openToken !== this._openToken) return;
        this._el.classList.add('is-open');
        this._updateThumbnailEdgeFades();
        this._updateActiveThumbnailIndicator(false);
        this._initFlickity(this._activeIndex, openToken);
      });
    });

    this._keyHandler = (e) => {
      if (e.key === 'Escape') {
        if (this._closeFeaturedProducts()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
        this.close();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        this._navigate(e.key === 'ArrowRight' ? 1 : -1);
      }
    };
    document.addEventListener('keydown', this._keyHandler);

    requestAnimationFrame(() => {
      this._el.querySelector('.pgl__close')?.focus();
    });
  }

  _updateCaption() {
    const captionEl = this._el?.querySelector('.pgl__caption');
    const flkty = this._getFlickity();
    if (!captionEl || !flkty) return;
    const caption = this._getCaption(flkty.selectedIndex);
    captionEl.textContent = caption;
    captionEl.title = caption;
  }

  _moveOverlayToSlide(index) {
    if (!this._overlay || !this._slider) return;

    const slide = this._flickity?.selectedElement || this._slider.children[index];
    const media = slide?.querySelector('.pgl__media');
    if (media && this._overlay.parentElement !== media) {
      media.appendChild(this._overlay);
    }
  }

  _bindResizeHandler() {
    if (this._resizeHandler) return;

    this._resizeHandler = () => {
      if (!this._isOpen) return;
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        if (!this._isOpen) return;
        this._flickity?.resize();
        this._updateThumbnailEdgeFades();
        this._updateActiveThumbnailIndicator(false);
      }, 100);
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  _unbindResizeHandler() {
    if (!this._resizeHandler) return;
    window.removeEventListener('resize', this._resizeHandler);
    this._resizeHandler = null;
    clearTimeout(this._resizeTimeout);
    this._resizeTimeout = null;
  }

  _notifyChange() {
    const flkty = this._getFlickity();
    if (!this._sourceGallery || !flkty) return;
    const sourceIndex = this._images[flkty.selectedIndex]?.sourceIndex ?? flkty.selectedIndex;
    this._sourceGallery.dispatchEvent(
      new CustomEvent('gallery-lightbox:change', {
        detail: { index: sourceIndex },
      })
    );
  }

  close() {
    if (!this._isOpen) {
      this._closeFeaturedProducts();
      document.body.classList.remove('pgl-lightbox-open');
      return;
    }

    const finalIndex = this._getFlickity()?.selectedIndex ?? 0;
    const sourceIndex = this._images[finalIndex]?.sourceIndex ?? finalIndex;
    this._openToken += 1;
    this._isOpen = false;

    this._el.classList.remove('is-open');
    this._closeFeaturedProducts();
    document.body.classList.remove('pgl-lightbox-open');
    document.body.style.overflow = '';

    if (this._keyHandler) {
      document.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }

    this._destroyFlickity();
    this._unbindResizeHandler();

    if (this._sourceGallery) {
      this._sourceGallery.dispatchEvent(
        new CustomEvent('gallery-lightbox:close', {
          detail: { index: sourceIndex },
        })
      );
      this._sourceGallery = null;
    }

    // Hide and clean up after transition completes
    setTimeout(() => {
      if (this._isOpen) return;
      this._el.setAttribute('hidden', '');
      this._slider.innerHTML = '';
      this._clearThumbnails();
      this._images = [];
    }, 380);
  }
}

const productGalleryLightbox = new ProductGalleryLightbox();

class FlickitySlider extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this._initialized = false;
    this._resizeObserver = null;
    this._resizeTimeout = null;
    this._prevEl = null;
    this._nextEl = null;
    this._prevHandler = null;
    this._nextHandler = null;
    this._navSelectHandler = null;
    this._navWrapAround = false;
  }

  connectedCallback() {
    this._initWithRetry();
    this._initCustomLightbox();
  }

  _initWithRetry(retries = 5) {
    if (window.Flickity) {
      this.initResponsive();
    } else if (retries > 0) {
      setTimeout(() => this._initWithRetry(retries - 1), 200);
    } else {
      console.warn('[FlickitySlider] Flickity library not found after retries.');
      this.classList.add('is-ready');
    }
  }

  initResponsive() {
    const isMobile = window.matchMedia('(max-width: 767px)');
    const script = this.querySelector(':scope > script[type="application/json"]');
    const settings = this._getSettings(script);
    const { prevBtn, nextBtn, disableDesktop, showStatus, ...options } = settings;
    this._navWrapAround = options.wrapAround === true;

    const finalOptions = {
      cellAlign: 'left',
      contain: true,
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      draggable: true,
      percentPosition: true,
      ...options,
    };

    const toggleSlider = (e) => {
      const container = this.querySelector('.flickity-slider-main');
      const target = container || this;

      if (!target) return;

      if (e.matches || !disableDesktop) {
        if (!this.slider && window.Flickity) {
          try {
            this.slider = new Flickity(target, finalOptions);
            this.classList.add('is-ready');
            this._setupNavigation(prevBtn, nextBtn);
            if (showStatus) this._setupStatus();

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                this.slider?.resize();
              });
            });

            this._setupResizeObserver(target);
          } catch (err) {
            console.error('[FlickitySlider] Init error:', err);
            this.classList.add('is-ready');
          }
        }
      } else {
        this._destroySlider();
        this.classList.add('is-ready');
      }
    };

    toggleSlider(isMobile);
    isMobile.addEventListener('change', toggleSlider);
  }

  _setupResizeObserver(target) {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    this._resizeObserver = new ResizeObserver(() => {
      clearTimeout(this._resizeTimeout);
      this._resizeTimeout = setTimeout(() => {
        if (this.slider) {
          this.slider.reloadCells();
          this.slider.resize();
          this._updateNavButtons();
        }
      }, 100);
    });

    this._resizeObserver.observe(target);
  }

  _destroySlider() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    clearTimeout(this._resizeTimeout);

    this._teardownNavigation();

    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
  }

  _setupStatus() {
    const status = this.querySelector('.flickity-slider-status');
    if (!status || !this.slider) return;

    const update = () => {
      const index = this.slider.selectedIndex + 1;
      const total = this.slider.cells.length;
      status.textContent = `${index}/${total}`;
    };

    this.slider.on('select', update);
    this.slider.on('ready', update);
    update();
  }

  _getSettings(script) {
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.error('[FlickitySlider] JSON parse error:', err);
      return {};
    }
  }

  _findNavElement(selector) {
    if (!selector) return null;
    if (selector.startsWith('#') && !selector.includes('.') && !selector.includes(' ')) {
      return document.getElementById(selector.substring(1));
    }
    return document.querySelector(selector);
  }

  _setupNavigation(prevSelector, nextSelector) {
    this._teardownNavigation();

    this._prevEl = this._findNavElement(prevSelector);
    this._nextEl = this._findNavElement(nextSelector);

    if (this._prevEl) {
      this._prevHandler = (e) => {
        e.preventDefault();
        this._goToAdjacent(-1);
      };
      this._prevEl.addEventListener('click', this._prevHandler);
    }

    if (this._nextEl) {
      this._nextHandler = (e) => {
        e.preventDefault();
        this._goToAdjacent(1);
      };
      this._nextEl.addEventListener('click', this._nextHandler);
    }

    if (this.slider) {
      this._navSelectHandler = () => this._updateNavButtons();
      this.slider.on('select', this._navSelectHandler);
      this.slider.on('change', this._navSelectHandler);
      this.slider.on('settle', this._navSelectHandler);
      this.slider.on('ready', this._navSelectHandler);
    }

    this._updateNavButtons();
  }

  _goToAdjacent(direction) {
    if (!this.slider) return;

    const cellCount = this.slider.cells.length;
    if (!cellCount) return;

    if (this._navWrapAround) {
      if (direction > 0) {
        this.slider.next();
      } else {
        this.slider.previous();
      }
      return;
    }

    const targetIndex = this.slider.selectedIndex + direction;

    if (targetIndex < 0 || targetIndex >= cellCount) return;

    this.slider.select(targetIndex);
  }

  _updateNavButtons() {
    if (!this.slider) return;

    const cellCount = this.slider.cells.length;
    const { selectedIndex } = this.slider;
    let canPrev = true;
    let canNext = true;

    if (!this._navWrapAround && cellCount > 0) {
      canPrev = selectedIndex > 0;
      canNext = selectedIndex < cellCount - 1;
    }

    if (this._prevEl) {
      this._prevEl.disabled = !canPrev;
      this._prevEl.setAttribute('aria-disabled', canPrev ? 'false' : 'true');
    }

    if (this._nextEl) {
      this._nextEl.disabled = !canNext;
      this._nextEl.setAttribute('aria-disabled', canNext ? 'false' : 'true');
    }

    const cellsWidth = this.slider.cells.reduce((sum, cell) => sum + cell.size.outerWidth, 0);
    const needsNav = cellsWidth > this.slider.size.innerWidth;
    this.classList.toggle('is-nav-visible', needsNav);
  }

  _teardownNavigation() {
    if (this._prevEl && this._prevHandler) {
      this._prevEl.removeEventListener('click', this._prevHandler);
    }
    if (this._nextEl && this._nextHandler) {
      this._nextEl.removeEventListener('click', this._nextHandler);
    }

    if (this.slider && this._navSelectHandler) {
      this.slider.off('select', this._navSelectHandler);
      this.slider.off('change', this._navSelectHandler);
      this.slider.off('settle', this._navSelectHandler);
      this.slider.off('ready', this._navSelectHandler);
    }

    this._prevEl = null;
    this._nextEl = null;
    this._prevHandler = null;
    this._nextHandler = null;
    this._navSelectHandler = null;
  }

  _initCustomLightbox() {
    if (!this.hasAttribute('data-product-images') || this.dataset.zoom === 'false') {
      return;
    }

    if (this.dataset.lightboxInit === 'true') {
      return;
    }
    this.dataset.lightboxInit = 'true';
    this.classList.add('pgl-enabled');

    const resolveImageIndex = (img, fallbackIndex = 0) => {
      const imgIdx = parseInt(img.getAttribute('data-index'), 10);
      if (!Number.isNaN(imgIdx) && imgIdx > 0) {
        return imgIdx - 1;
      }

      const slideIdx = parseInt(
        img.closest('.flickity-slider-slide, .product-main-slide')?.getAttribute('data-index'),
        10
      );
      if (!Number.isNaN(slideIdx)) {
        return slideIdx;
      }

      return fallbackIndex;
    };

    // Deduplicate by 0-based slide index (handles Flickity's cloned cells for wrapAround)
    const getImages = () => {
      const map = new Map();
      this.querySelectorAll('.photoswipe__image').forEach((img, i) => {
        const idx = resolveImageIndex(img, i);
        if (!map.has(idx)) {
          map.set(idx, {
            sourceIndex: idx,
            src: img.getAttribute('data-photoswipe-src') || img.src,
            alt: img.alt,
            caption: img.dataset.caption || '',
          });
        }
      });
      return Array.from(map.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, image]) => image);
    };

    const openAt = (index) => productGalleryLightbox.open(getImages(), index, this);

    // Use event delegation to handle both original and Flickity-cloned elements
    this.addEventListener('click', (e) => {
      if (e.target.closest('.js-gallery-trigger')) {
        openAt(0);
        return;
      }

      if (e.target.closest('.js-photoswipe__zoom')) {
        let index = 0;
        const flickityEl = this.querySelector('.flickity-slider-main');
        const flickityInstance = flickityEl && window.Flickity && Flickity.data(flickityEl);
        if (flickityInstance) {
          index = flickityInstance.selectedIndex;
        } else {
          const img = e.target.closest('.flickity-slider-slide')?.querySelector('.photoswipe__image');
          if (img) {
            index = resolveImageIndex(img, 0);
          }
        }
        openAt(index);
        return;
      }

      const img = e.target.closest('.photoswipe__image');
      if (img) {
        openAt(resolveImageIndex(img, 0));
      }
    });

    // Sync mobile Flickity slider when lightbox closes
    this.addEventListener('gallery-lightbox:close', (e) => {
      const flickityEl = this.querySelector('.flickity-slider-main');
      const flickityInstance = flickityEl && window.Flickity && Flickity.data(flickityEl);
      if (flickityInstance) {
        flickityInstance.select(e.detail.index);
      }
    });
  }

  disconnectedCallback() {
    this._destroySlider();
  }
}

if (!customElements.get('flickity-slider')) {
  customElements.define('flickity-slider', FlickitySlider);
}
